import { useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { RootState } from 'app/store';
import {
  openToolbarWindow,
  closeToolbarWindow,
  setPinnedViews,
  setCanvasView,
  setAutoRotate,
  setRotateEnabled,
  openDuplicateAccessoryPopup,
  openDeleteAccessoryPopup,
  type AhuToolbarWindow,
  type AhuViewPreset,
} from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import {
  readPinnedView,
  writePinnedView,
  selectModel3d,
  selectModel3dTechData,
} from '../ahuBoardStorage';
import { alignAhuTunnels } from '../alignTunnels';
import { alignHybridTunnelsX } from '../boardActions';
import ErrorManager from './toolbar/ErrorManager';
import ConfigureTunnel from './toolbar/ConfigureTunnel';
import DrawingWindow from './toolbar/DrawingWindow';
import HybridInfoPopup, { type HybridInfoState } from './toolbar/HybridInfoPopup';

/**
 * Rail densities, loosest first — `[data-density]` in ahuBoardOverrides.scss
 * sets the gap and the groups' vertical padding for each. See the effect that
 * picks one in CanvasToolbar below.
 */
const RAIL_DENSITIES = ['normal', 'compact', 'tight', 'micro'] as const;

/** §2.2 views.tmpl — order + labels (UI list; breadcrumb order differs, §2.1). */
const VIEWS_LIST: Array<{ value: AhuViewPreset; label: string }> = [
  { value: 'default', label: 'Isometric Front Right' },
  { value: 'front_left', label: 'Isometric Front Left' },
  { value: 'back_left', label: 'Isometric Back Left' },
  { value: 'back_right', label: 'Isometric Back Right' },
  { value: 'plan', label: 'Plan' },
  { value: 'elevation_near_side', label: 'Elevation Near Side' },
];

const Model3dSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="-14.204 -5.726 540.408 523.452">
    <path
      d="M 275.144 337.376 C 260.301 323.541 267.22 328.379 252.454 314.5 C 251.721 313.689 250.911 314.608 250.354 315.046 C 233.601 330.678 213.071 342.609 190.767 349.484 C 165.091 357.41 137.194 358.656 110.844 353.139 C 63.941 343.549 22.861 311.391 4.007 269.315 C 4.007 269.315 4.047 269.315 4.047 269.293 C 2.484 266.316 0.83 262.946 1.598 259.462 C 4.199 247.664 11.525 242.082 22.05 238.842 C 32.347 235.671 42.428 256.858 46.591 262.179 C 69.185 292.192 86.862 302.963 117.668 310.189 C 153.331 318.768 194.758 309.946 220.705 284.727 C 220.724 283.894 215.79 273.014 203.972 260.209 C 223.77 258.5 254.343 247.137 296.406 241.644 C 291.838 280.217 280.945 313.689 275.144 337.376 Z"
      style={{ fill: '#BDBDBD' }}
      transform="matrix(0.48481, 0.87462, -0.87462, 0.48481, 336.931335, 23.044296)"
    />
    <text
      style={{
        fontFamily: 'Arial, sans-serif',
        fontSize: 151,
        fontWeight: 700,
        stroke: 'rgb(0, 0, 0)',
        whiteSpace: 'pre',
      }}
      x="185.519"
      y="291.785"
    >
      3D
    </text>
    <path
      d="M 521.368 222.145 C 506.525 208.31 513.444 213.148 498.678 199.269 C 497.945 198.458 497.135 199.377 496.578 199.815 C 479.825 215.447 459.295 227.378 436.991 234.253 C 411.315 242.179 383.419 243.425 357.069 237.908 C 310.166 228.318 269.086 196.16 250.232 154.084 C 250.232 154.084 250.272 154.084 250.272 154.062 C 248.709 151.085 247.055 147.715 247.823 144.231 C 250.424 132.433 257.75 126.851 268.275 123.611 C 278.572 120.44 288.653 141.627 292.816 146.948 C 315.41 176.961 333.087 187.732 363.893 194.958 C 399.555 203.537 440.982 194.715 466.929 169.496 C 466.948 168.663 462.014 157.783 450.196 144.978 C 469.994 143.269 500.567 131.906 542.63 126.413 C 538.062 164.986 527.169 198.458 521.368 222.145 Z"
      style={{ fill: '#BDBDBD' }}
      transform="matrix(-0.48481, -0.87462, 0.87462, -0.48481, 427.258392, 616.252686)"
    />
  </svg>
);

/**
 * Equalize-tunnel-lengths icon, **hybrid AHUs only** (§8.6). Legacy stacks two
 * Material Symbols glyphs — `horizontal_align_left` + `horizontal_align_right`,
 * overlapped by the ±6px `left` in ahu/skeleton.css:358-365 — and each of those
 * glyphs carries a vertical end bar, so the button reads as `|<->|`. Requested
 * 2026-09-28: on a hybrid AHU keep only the arrow (and retitle the tooltip);
 * standard AHUs keep the legacy pair and the legacy wording untouched.
 *
 * The first cut used Material's own `arrow_range` — the same pair of heads
 * with no bars — but that glyph's arrow is 18.3px wide against the 21px the
 * stacked pair draws, so the hybrid button came out visibly shorter than the
 * standard one. Requested 2026-09-29: same length as the standard button,
 * bars aside, with the arrow back at the length it had while the bars were
 * there. So the icon is now literally the standard pair **with the two bar
 * subpaths deleted** — both glyphs' arrow outlines, at the offsets the ±6px
 * `left` rules give them — rather than a different glyph sized to match.
 *
 * Geometry (all of it forced by the two rules above, nothing tuned by eye):
 *  - Each glyph is a 960-unit em box rendered at font-size 22px, so one px is
 *    960/22 = 43.6364 units. `justify-content: center` on the 36px label lays
 *    the two 22px boxes out overflowing to [-4,18] and [18,40]; the ±6px
 *    `left` then shifts them to [2,24] and [12,34].
 *  - Hence the second glyph sits 10px = 436.36 units right of the first, which
 *    is the `translate` below, and the pair's combined box is 32px wide
 *    (1396.36 units) — the `width` in ahuBoardOverrides.scss.
 *  - Flex-centred, that 32px box lands back on [2,34], so the arrows' ink runs
 *    [7.5, 28.5] — pixel for pixel where the standard button's arrow runs,
 *    with only the bars (which sat at [4.75,6.13] and [29.88,31.25]) gone.
 *
 * Inlined rather than added as a third ligature so it cannot depend on the
 * Google Fonts subset the page happens to get, and so the
 * `.material-symbols-outlined` offsets above no longer apply.
 */
const AlignTunnelsArrowSvg = () => (
  <svg
    className="align-tunnels-arrow"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 -960 1396.36 960"
    fill="currentColor"
  >
    {/* `horizontal_align_left` minus its `M120-160v-640h60v640h-60Z` bar. */}
    <path d="M430-290 240-480 434-674l43 43-121 121h484v60H356l117 117-43 43Z" />
    {/* `horizontal_align_right` minus its `M780-160v-640h60v640h-60Z` bar. */}
    <path
      transform="translate(436.36 0)"
      d="M526-286l-43-43 121-121H120v-60h484L487-627l43-43 190 190-194 194Z"
    />
  </svg>
);

interface CanvasToolbarProps {
  ahuId: number;
  tunnelsCount: number;
  /** Empty-AHU chrome hide (render_canvas §3.1 / parity 16): jQuery .hide() port. */
  hiddenForEmptyState?: boolean;
  /** 6e1 — window contents (Error Manager / Configure Tunnel / equalize). */
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  /**
   * View-only *purely* because of the hybrid Standard-user gate. The Drawing
   * window (#dwg_param) is then re-armed with the legacy `.clickable` escape
   * hatch (styles.css) so its Settings / Callout Schedule blocks stay editable
   * — they feed Model View generation, not the AHU config the gate protects.
   */
  hybridStandardLock?: boolean;
  /**
   * `ahu.is_hybrid` — the AHU's own flag, the same one the `(hybrid)` badge
   * beside the tag reads. Only the equalize button's icon + tooltip depend on
   * it (requested 2026-09-28); a standard AHU keeps the legacy pair of glyphs
   * and "Equalize tunnel lengths".
   */
  ahuIsHybrid?: boolean;
}

/**
 * #canvas_toolbar — the right rail (main.tmpl §2.1, markup faithful) + the
 * MainView single-open-window engine (§2.4 toolbarElementClick/closeWindow,
 * parity 20-28). 6a scope: windows are SKELETONS — Views is fully rendered
 * (views.tmpl §2.2, it is pure chrome); Error Manager / Configure Tunnel /
 * Drawing / Generate window CONTENTS are 6e mounts. Buttons whose features
 * land later are present but inert per the build order.
 *
 * Engine parity notes:
 *  - Exactly one window open: `.toolbar-item` ⇄ `.opacity_0`, `.with_window`
 *    ⇄ `.active`, `.input_toolbar` checkboxes — all derived from
 *    `toolbar.openWindow` (the legacy class/checkbox protocol, parity 21).
 *  - Re-click of an open window's label closes it; closing while pinned
 *    re-opens Views (closeWindow §2.4).
 *  - 600 ms hover-open on `.with_window` labels; generate_3d only when
 *    `.enable-generate` (never in 6a — gating arrives with the error log 6e).
 *  - Outside click closes (document listener); `.toolbar-item` clicks stop
 *    propagation. The error-log 550 ms `clicked` debounce ships with the
 *    error-log content (6e).
 *  - Zoom labels emit `canvas:zoom` on the bus (§3.4 #11; consumer = 6b) and,
 *    like legacy's class sweep, close any open window.
 *  - Views list: `.active` swap + `canvas:set:view (value, true)`; re-click of
 *    the current value closes the window; pin persists sessionStorage
 *    `pinned-view` and re-opens on load (parity 24-25).
 *  - Generate window labels write localStorage `3d`/`3d_td` (mutually
 *    exclusive, parity 28). DEVIATION (read-only 6a): the labels are NOT
 *    wired to #ahu3dGenerate (no htmlFor), so no POST /model/generate/ can
 *    fire; the htmlFor wiring returns with generate gating in 6e.
 *  - #auto_360 starts `.disable.disable_rotate.hide` (legacy initial state);
 *    its enable flows are canvas-side (6b).
 *  - align-tunnel li `.non-clickable` when tunnels < 2 (parity 26).
 *  - #accessories-ul armed via `select:accessory` → slice (parity 27).
 */
const CanvasToolbar = ({
  ahuId,
  tunnelsCount,
  hiddenForEmptyState,
  tunnels,
  sections,
  hybridStandardLock,
  ahuIsHybrid,
}: CanvasToolbarProps) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const openWindow = useAppSelector((s) => s.ahuBoard.toolbar.openWindow);
  const pinned = useAppSelector((s) => s.ahuBoard.toolbar.pinnedViews);
  const currentView = useAppSelector((s) => s.ahuBoard.canvas.view);
  const autoRotate = useAppSelector((s) => s.ahuBoard.canvas.autoRotate);
  const rotateEnabled = useAppSelector((s) => s.ahuBoard.canvas.rotateEnabled);
  const selectedAccessoryId = useAppSelector((s) => s.ahuBoard.selectedAccessoryId);
  // 6e1 — error-manager gating (§8.1 changeGenerateButton, parity 148/154).
  const errorCount = useAppSelector((s) => s.ahuValidation.count);
  const allowGenerate = useAppSelector((s) => s.ahuValidation.allowGenerate);
  const validating = useAppSelector((s) => s.ahuValidation.validatingCount > 0);
  // First validation completed? (revision bumps on every slice merge.)
  const hasValidated = useAppSelector((s) => s.ahuValidation.revision > 0);
  const [aligning, setAligning] = useState(false);
  /**
   * The hybrid Info window (requested 2026-09-30). Deliberately NOT part of
   * `toolbar.openWindow`: it is draggable, it has its own Minimize/Close, and
   * an outside click must not dismiss it — see HybridInfoPopup.
   */
  const [infoState, setInfoState] = useState<HybridInfoState>('closed');

  const hoverTimer = useRef<number | null>(null);
  const infoHoverTimer = useRef<number | null>(null);

  /**
   * Rail density (requested 2026-10-02). The rail is a stack of thirteen fixed
   * 36px rows plus fixed paddings — nothing in it can give way — so when the
   * board area gets shorter than the stack, `min-height: calc(100% - 28px)`
   * (ahu/skeleton.css:570-582) lets it grow straight past the bottom of that
   * area. Measured on a 1920×1080 screen: at 110% browser zoom the rail is
   * 736px in 686px of room and its last 36px lie over the footer strip; at
   * 125% it ends 36px BELOW the window, and since `body` is `overflow:
   * hidden` nothing scrolls the Info button back into view.
   *
   * Browser zoom is also the one case the board's own body-zoom cannot
   * absorb: `applyBodyZoom` (useBodyZoom.ts) pins the zoom to 1 as soon as
   * devicePixelRatio goes over 100%, which is exactly what zooming in does.
   *
   * So the rail gives up the air around the rows first — 736px → 656 → 576,
   * which covers 125% on a 1080p screen with the 36px row untouched — and
   * only then the row itself (36 → 28), which gets 150% in at 444px. Every
   * step keeps the group boundaries on the 4px grid the separators need (see
   * ahuBoardOverrides.scss). Past ~175% nothing fits and the rail clips, the
   * same way the rest of the board does at that zoom.
   *
   * Picked by measurement rather than by a height media query, because the
   * query would read the window while the rail lives in a body that may be
   * scaled by the body zoom — the same window height means different room
   * for the rail at each.
   */
  const railRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const rail = railRef.current;
    const host = rail?.parentElement;
    if (!rail || !host) return;
    const fit = () => {
      // Hidden rail (empty AHU) measures 0 — leave the density alone.
      if (!host.clientHeight || !rail.clientHeight) return;
      const room = host.clientHeight - 28; // the 14px inset top and bottom
      for (const density of RAIL_DENSITIES) {
        rail.dataset.density = density;
        if (rail.scrollHeight <= room) return;
      }
    };
    fit();
    // The host is the board area; it never resizes because of the rail, so
    // this cannot feed back into itself.
    const observer = new ResizeObserver(fit);
    observer.observe(host);
    return () => observer.disconnect();
  }, [ahuIsHybrid, hiddenForEmptyState]);

  // Generate gating classes — legacy state machine (QA batch 4 #10/#11):
  //  - validating → `disable` (activateValidating, unit.js:744-751: strips
  //    enable/disable-generate, adds `disable` — grey, not clickable);
  //  - before the first validation → bare (main.tmpl:69 initial classes);
  //  - after: allow → `enable-generate` (disable removed, error.log.js:290);
  //    blocked → `disable disable-generate` (changeGenerateButton's else keeps
  //    the `disable` activateValidating added — verified live on legacy).
  const generateClass = validating
    ? ' disable'
    : !hasValidated
      ? ''
      : allowGenerate
        ? ' enable-generate'
        : ' disable disable-generate';
  // Click gating ≡ the legacy delegated selector `li:not(.disable):not(
  // .non-clickable):not(.disable-generate) label` (main.js:96).
  const generateClickable = !generateClass.includes('disable');
  // #li_error_log non-clickable at 0 errors or while validating (parity 148).
  const errorLogLocked = errorCount === 0 || validating;

  // §8.6 — one toolbar button, two different operations.
  //
  // Standard AHU: "Equalize tunnel lengths", legacy's AlignTunnelView — grows
  // short tunnels' sections until every tunnel is as long as the longest, all
  // of it computed client-side (alignTunnels.ts).
  //
  // Hybrid AHU: "Align All Sections in X Direction" — the tunnels keep their
  // lengths; what moves is where each one SITS along X, and that solve is
  // server-side (POST units/<id>/align_tunnels_x/, applied to `x_offset`).
  // The two share nothing but the button, so they fork here rather than inside
  // alignAhuTunnels. The icon and tooltip already forked on this same flag
  // (requested 2026-09-28); this is the behaviour behind them.
  const onAlignTunnels = () => {
    if (tunnelsCount < 2 || aligning) return;
    if (ahuIsHybrid) {
      setAligning(true);
      void dispatch(alignHybridTunnelsX(ahuId)).finally(() => setAligning(false));
      return;
    }
    setAligning(true);
    const getContext = () => {
      const sectionsByTunnel: Record<number, IAhuBoardSection[]> = {};
      const state = store.getState();
      const queries = (state['ahuBoardApi' as keyof RootState] as unknown as {
        queries: Record<string, { endpointName?: string; data?: unknown } | undefined>;
      }).queries;
      tunnels.forEach((t) => {
        sectionsByTunnel[t.id] = [];
      });
      Object.entries(queries).forEach(([key, q]) => {
        if (q?.endpointName === 'getTunnelSectionList' && Array.isArray(q.data)) {
          const match = key.match(/\((\d+)\)/);
          if (match) sectionsByTunnel[Number(match[1])] = q.data as IAhuBoardSection[];
        }
      });
      return { tunnels, sectionsByTunnel };
    };
    void alignAhuTunnels(dispatch, getContext).finally(() => setAligning(false));
  };

  // unit.js deActivateValidating :781-789 — 3 s after validation settles the
  // rail #auto_360 loses `disable` (the legacy accessory-counter gate is the
  // documented §useAccessoryForm #352 dedrift). Without this port a board
  // whose drawTunnelBase clears early-return (lug tunnels with hidden/missing
  // lug rows) never enables 360 at all (QA batch 4 #2).
  const rotateTimerRef = useRef<number | null>(null);
  const wasValidatingRef = useRef(false);
  useEffect(() => {
    if (wasValidatingRef.current && !validating) {
      if (rotateTimerRef.current) window.clearTimeout(rotateTimerRef.current);
      rotateTimerRef.current = window.setTimeout(() => {
        dispatch(setRotateEnabled(true));
        rotateTimerRef.current = null;
      }, 3000);
    }
    wasValidatingRef.current = validating;
    return () => {
      if (rotateTimerRef.current) {
        window.clearTimeout(rotateTimerRef.current);
        rotateTimerRef.current = null;
      }
    };
  }, [validating, dispatch]);

  // Pinned Views re-opens on load (MainView init `if (pinned) $('.li_views label').click()`).
  useEffect(() => {
    const isPinned = readPinnedView();
    if (isPinned) {
      dispatch(setPinnedViews(true));
      dispatch(openToolbarWindow('views'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outside click closes the open window AND stops 360 (the §2.4 document.body
  // listener — main.js:365 also calls auto360() off unless the click was the
  // 360 control itself; toolbar/window clicks stopPropagation below so they
  // never reach here). Pinned keeps/restores Views.
  useEffect(() => {
    const onDocClick = () => {
      if (autoRotate) {
        dispatch(setAutoRotate(false));
        ahuBoardBus.emit('canvas:rotate', false);
      }
      if (!openWindow) return;
      if (pinned) {
        if (openWindow !== 'views') dispatch(openToolbarWindow('views'));
      } else {
        dispatch(closeToolbarWindow());
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openWindow, pinned, autoRotate, dispatch]);

  const stopAutoRotateIfNeeded = (tool: string) => {
    // Selecting any tool while autoRotate (except views) stops 360 (§2.4).
    if (autoRotate && tool !== 'views') {
      dispatch(setAutoRotate(false));
      ahuBoardBus.emit('canvas:rotate', false);
    }
  };

  const closeWindowLikeLegacy = () => {
    dispatch(closeToolbarWindow());
    if (pinned) dispatch(openToolbarWindow('views'));
  };

  const onWindowLabelClick = (e: React.MouseEvent, tool: AhuToolbarWindow) => {
    e.stopPropagation();
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    if (openWindow === tool) {
      closeWindowLikeLegacy();
      return;
    }
    stopAutoRotateIfNeeded(tool);
    dispatch(openToolbarWindow(tool));
    // title_block focus of `#dwg_param input.first-input` (§2.4) — the drawing
    // window content is a 6e mount; optional-chained no-op until then.
    if (tool === 'title_block') {
      (document.querySelector('#dwg_param input.first-input') as HTMLElement | null)?.focus();
    }
  };

  const onHoverOpen = (tool: AhuToolbarWindow, enabled: boolean) => {
    if (!enabled || openWindow === tool) return;
    hoverTimer.current = window.setTimeout(() => {
      stopAutoRotateIfNeeded(tool);
      dispatch(openToolbarWindow(tool));
      // Hover-opening a window is opening it (reported 2026-10-05) — see
      // onRailClickCapture for why the How to window has to be told.
      setInfoState((s) => (s === 'open' ? 'closed' : s));
    }, 600);
  };

  const onHoverLeave = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
  };

  // Info (hybrid only) — "when hovering over the Info icon, open an Info
  // popup, similar to the existing Configure Tunnel popup", so the same 600 ms
  // hover-open the `.with_window` labels use. A click opens it too (and closes
  // it again on re-click, matching onWindowLabelClick); a minimized window is
  // restored by its floating icon, not by hovering the rail.
  const onInfoHoverOpen = () => {
    if (infoState !== 'closed') return;
    infoHoverTimer.current = window.setTimeout(() => setInfoState('open'), 600);
  };

  const onInfoHoverLeave = () => {
    if (infoHoverTimer.current) window.clearTimeout(infoHoverTimer.current);
  };

  const onInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (infoHoverTimer.current) window.clearTimeout(infoHoverTimer.current);
    setInfoState((s) => (s === 'open' ? 'closed' : 'open'));
  };

  /**
   * Using any OTHER rail button closes the open How to window (reported
   * 2026-10-05). The window is deliberately outside the single-open
   * `openWindow` engine, so it used to survive opening Views / Error Manager /
   * Configure Tunnel / Drawing / Generate and sat on top of them; and because
   * `li span.li_tooltip` starts at `left: 45px` while the window docks at
   * `left: 75px` (ahu/skeleton.css:592/1063), every rail tooltip wider than
   * 30px — "Delete Section", "Duplicate Accessory", … — came out half-hidden
   * behind it. One handler on the rail rather than one per button, in the
   * CAPTURE phase because the buttons' own handlers stopPropagation.
   *
   * Scoped to `ul.list` so it is the rail's own rows that close it: the docked
   * windows' contents are children of `#canvas_toolbar` too (none of them uses
   * the bare `list` class), and clicking inside Error Manager or Configure
   * Tunnel is not "using another button".
   *
   * A MINIMIZED window is left alone — it is already out of the way, and
   * closing it would throw away the minimize. Its pending hover-open, though,
   * is always cancelled, or the window would pop open 600 ms after the user
   * moved on to another button.
   */
  const onRailClickCapture = (e: React.MouseEvent) => {
    const row = (e.target as HTMLElement).closest('ul.list > li');
    if (!row || row.classList.contains('li_hybrid_info')) return;
    if (infoHoverTimer.current) window.clearTimeout(infoHoverTimer.current);
    if (infoState === 'open') setInfoState('closed');
  };

  useEffect(
    () => () => {
      if (infoHoverTimer.current) window.clearTimeout(infoHoverTimer.current);
    },
    []
  );

  const onZoom = (e: React.MouseEvent, direction: 'zoom-in' | 'zoom-out') => {
    e.stopPropagation();
    // Legacy class sweep closes any open window on non-window tool clicks.
    if (openWindow) closeWindowLikeLegacy();
    ahuBoardBus.emit('canvas:zoom', direction);
  };

  // defineCanvasViewport (main.js:805): `.active` swap + canvas:set:view fires
  // on EVERY list click — including a re-click of the active value, which
  // additionally closes the window (6b fix of the 6a early-return deviation).
  const onViewsListClick = (value: AhuViewPreset) => {
    const prevActive = currentView;
    dispatch(setCanvasView(value));
    ahuBoardBus.emit('canvas:set:view', value, true);
    if (value === prevActive) closeWindowLikeLegacy();
  };

  // MainView auto360() (§2.4): toggle + `canvas:rotate` on the bus; the 6b
  // canvas consumer snaps to the default view and swaps to the fixed grid.
  // Gated by rotateEnabled (the legacy `disable`/`disable_rotate` classes).
  const toggleAuto360 = () => {
    if (!rotateEnabled) return;
    const next = !autoRotate;
    dispatch(setAutoRotate(next));
    ahuBoardBus.emit('canvas:rotate', next);
  };

  const onPinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !pinned;
    dispatch(setPinnedViews(next));
    writePinnedView(next);
  };

  const liActive = (tool: AhuToolbarWindow) => (openWindow === tool ? ' active' : '');
  const itemHidden = (tool: AhuToolbarWindow) => (openWindow === tool ? '' : ' opacity_0');
  const accessoriesArmed = selectedAccessoryId != null;

  return (
    <div
      id="canvas_toolbar"
      ref={railRef}
      data-density="normal"
      style={hiddenForEmptyState ? { display: 'none' } : undefined}
      onClickCapture={onRailClickCapture}
    >
      <div>
        <ul className="list border-bottom">
          <li
            className={`li_model_3d generate_3d with_window${generateClass}${liActive('model_3d')}`}
            data-type="model_3d"
          >
            {/* Hover-open gated on .enable-generate (§2.4 / parity 154);
                click gated like the legacy delegated selector (main.js:96 —
                `.disable`/`.disable-generate` labels never fire). */}
            <label
              data-type="model_3d"
              onClick={(e) => {
                // Not clickable → let the click bubble to the document
                // listener (closes windows / stops 360) exactly like legacy.
                if (generateClickable) onWindowLabelClick(e, 'model_3d');
              }}
              onMouseOver={() =>
                onHoverOpen('model_3d', generateClass === ' enable-generate')
              }
              onMouseLeave={onHoverLeave}
            >
              <Model3dSvg />
              <span className="li_tooltip">Generate 3D Model</span>
              <span className="arrow-right-wrapper">
                <i className="icon-ic-keyboard-arrow-right-black-24px" />
              </span>
            </label>
          </li>
        </ul>
        <ul className="list border-bottom actions">
          <li className={`with_window icon-views li_views${liActive('views')}`}>
            <label
              data-type="views"
              onClick={(e) => onWindowLabelClick(e, 'views')}
              onMouseOver={() => onHoverOpen('views', true)}
              onMouseLeave={onHoverLeave}
            >
              <span className="li_tooltip">Views</span>
              <span className="arrow-right-wrapper">
                <i className="icon-ic-keyboard-arrow-right-black-24px" />
              </span>
            </label>
          </li>
          <li className="icon-zoom-in-1">
            <label
              data-type="zoom-in"
              className="zoom-in-out"
              onClick={(e) => onZoom(e, 'zoom-in')}
            >
              <span className="li_tooltip">Zoom In</span>
            </label>
          </li>
          <li className="icon-zoom-out-1">
            <label
              data-type="zoom-out"
              className="zoom-in-out"
              onClick={(e) => onZoom(e, 'zoom-out')}
            >
              <span className="li_tooltip">Zoom Out</span>
            </label>
          </li>
          {/* Rail 360 li: `.hide` is NEVER removed in legacy (users reach 360
              via the views-list entry below); disable/disable_rotate now track
              rotateEnabled (cleared by draw paths in legacy — §3.5). */}
          <li
            className={`auto_360 li_auto_360 with_window hide${
              rotateEnabled ? '' : ' disable disable_rotate'
            }${autoRotate ? ' active' : ''}`}
            id="auto_360"
          >
            <label
              data-type="auto_360"
              onClick={(e) => {
                e.stopPropagation();
                toggleAuto360();
              }}
            >
              360
              <span className="li_tooltip for_not_disable">
                Auto 360<sup>o</sup> Rotation
              </span>
              <span className="li_tooltip for_disable">
                Auto 360<sup>o</sup> Rotation
              </span>
            </label>
          </li>
        </ul>
        <ul className="list">
          <li
            className={`with_window li_error_log icon-error-log${liActive('error_log')}${
              errorLogLocked ? ' non-clickable' : ''
            }`}
            id="li_error_log"
          >
            <label
              data-type="error_log"
              onClick={(e) => {
                if (!errorLogLocked) onWindowLabelClick(e, 'error_log');
              }}
              onMouseOver={() => onHoverOpen('error_log', !errorLogLocked)}
              onMouseLeave={onHoverLeave}
            >
              <span className="li_tooltip">Error Manager</span>
              <span
                className={`error-count${
                  errorCount > 0 && allowGenerate ? ' only-warnings' : ''
                }`}
                data-count="count"
              >
                {' '}
                <span className="count" data-count="count">
                  {errorCount || ''}
                </span>
              </span>
              <span className="arrow-right-wrapper">
                <i className="icon-ic-keyboard-arrow-right-black-24px" />
              </span>
            </label>
          </li>
          <li
            className={`with_window li_configure_tunnel icon-configure-tunnel conected-to-error-log${liActive(
              'configure_tunnel'
            )}`}
          >
            <label
              data-type="configure_tunnel"
              onClick={(e) => onWindowLabelClick(e, 'configure_tunnel')}
              onMouseOver={() => onHoverOpen('configure_tunnel', true)}
              onMouseLeave={onHoverLeave}
            >
              <span className="li_tooltip">Configure Tunnel Sections</span>
              <span className="arrow-right-wrapper">
                <i className="icon-ic-keyboard-arrow-right-black-24px" />
              </span>
            </label>
          </li>
          <li className={`with_window li_title_block icon-subtitle conected-to-error-log${liActive('title_block')}`}>
            <label
              data-type="title_block"
              onClick={(e) => {
                e.preventDefault();
                onWindowLabelClick(e, 'title_block');
              }}
              onMouseOver={() => onHoverOpen('title_block', true)}
              onMouseLeave={onHoverLeave}
            >
              <span className="li_tooltip">Drawing</span>
              <span className="arrow-right-wrapper">
                <i className="icon-ic-keyboard-arrow-right-black-24px" />
              </span>
            </label>
          </li>
        </ul>
      </div>
      <div>
        <ul className="list list_bottom">
          {/* AlignTunnelView el (§8.6); gating is parity 26. */}
          <li
            className={`border-top align-tunnel-li${
              tunnelsCount < 2 || aligning ? ' non-clickable' : ''
            }`}
          >
            <label
              data-type="align-tunnel"
              className="align-items-icon align-tunnels"
              onClick={onAlignTunnels}
            >
              {/* Hybrid only — standard AHUs keep legacy's bar-carrying pair
                  and legacy's wording (requested 2026-09-28). */}
              {ahuIsHybrid ? (
                <AlignTunnelsArrowSvg />
              ) : (
                <>
                  <span className="material-symbols-outlined">horizontal_align_left</span>
                  <span className="material-symbols-outlined">horizontal_align_right</span>
                </>
              )}
              <span className="li_tooltip">
                {ahuIsHybrid ? 'Align All Sections in X Direction' : 'Equalize tunnel lengths'}
                <span className="section_info" />
              </span>
            </label>
          </li>
        </ul>
        <ul
          className="list list_bottom conected-to-error-log"
          id="accessories-ul"
          {...(accessoriesArmed ? { 'data-id': selectedAccessoryId } : {})}
        >
          <li className={`icon-copy border-top${accessoriesArmed ? '' : ' non-clickable'}`}>
            <label
              data-type="duplicate"
              onClick={() => {
                // §13 row 12 — MainView showDuplicatePopup proxy.
                if (accessoriesArmed)
                  dispatch(openDuplicateAccessoryPopup(selectedAccessoryId));
              }}
            >
              <span className="li_tooltip">
                Duplicate Accessory
                <span className="accessory_info" />
              </span>
            </label>
          </li>
          <li className={`icon-trash${accessoriesArmed ? '' : ' non-clickable'}`}>
            <label
              data-type="delete"
              onClick={() => {
                // §13 row 12 — MainView openDeleteAccessoryPopup proxy.
                if (accessoriesArmed)
                  dispatch(openDeleteAccessoryPopup(selectedAccessoryId));
              }}
            >
              <span className="li_tooltip">
                Delete Accessory
                <span className="accessory_info" />
              </span>
            </label>
          </li>
        </ul>
        <ul className="list list_bottom conected-to-error-log" id="sections-ul">
          <li className="icon-copy border-top">
            <label htmlFor="duplicate_section" data-type="duplicate">
              <span className="li_tooltip">
                Duplicate Section
                <span className="section_info" />
              </span>
            </label>
          </li>
          <li className="icon-trash">
            <label htmlFor="delete_section" data-type="delete">
              <span className="li_tooltip">
                Delete Section<span className="section_info_all hide">s</span>
                <span className="section_info" />
              </span>
            </label>
          </li>
        </ul>
        {/* Info — hybrid AHUs only (requested 2026-09-30): the copy behind it
            is the Hybrid Configuration "How to" (rows/levels/positions, the
            X/Y/Z origins, the offset modes), none of which applies to a
            standard unit. Its own `ul.list_bottom` rather than a third li in
            #sections-ul, because `ul.list_bottom { border-top }`
            (ahu/skeleton.css:938-940) IS the separating line the rail already
            draws between groups — and because #sections-ul stamps an "S"
            superscript on every child li (:960-962), which this icon must not
            inherit. */}
        {ahuIsHybrid && (
          <ul className="list list_bottom" id="info-ul">
            <li className={`icon-info li_hybrid_info${infoState === 'open' ? ' active' : ''}`}>
              <label
                data-type="info"
                onClick={onInfoClick}
                onMouseOver={onInfoHoverOpen}
                onMouseLeave={onInfoHoverLeave}
              >
                {/* Requested 2026-09-30 — same wording as the floating icon's
                    hover title, so both affordances name the same window. */}
                <span className="li_tooltip">How to Hybrid</span>
              </label>
            </li>
          </ul>
        )}
      </div>
      <input type="checkbox" className="hidden input_toolbar input_auto_360" readOnly checked={false} />

      {/* ---- Toolbar windows (single-open; contents per §2.1/§2.2) ---- */}
      <div
        className={`new-section-wrapper dropdown-wrapper toolbar-item configure_tunnel${itemHidden(
          'configure_tunnel'
        )}`}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          id="section_ctrl"
          type="checkbox"
          name="section_ctrl"
          className="hidden input_toolbar input_configure_tunnel"
          checked={openWindow === 'configure_tunnel'}
          readOnly
        />
        <label htmlFor="section_ctrl" className="icon-expand ka-has-tooltip hide" tooltip="Expand" />
        <div className="title">Configure Tunnel</div>
        {/* ViewTunnelsSection (§8.2). */}
        <div className="new-closed-section" id="sections_order_list">
          <ConfigureTunnel tunnels={tunnels} sections={sections} />
        </div>
      </div>

      <div
        id="error_log"
        className={`toolbar-item error_log${itemHidden('error_log')}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ViewErrorLog (§8.1 — renders the full error.log.tmpl wrapper). */}
        <ErrorManager tunnels={tunnels} sections={sections} />
      </div>

      <div
        id="dwg_param"
        className={`dwg-model-params col-xs-12 toolbar-item title_block${
          hybridStandardLock ? ' clickable' : ''
        }${itemHidden('title_block')}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ViewDrawingInfo (§8.3). */}
        <div className="col-xs-12 p-0">
          <DrawingWindow ahuId={ahuId} hybridStandardLock={hybridStandardLock} />
        </div>
      </div>

      <div
        id="views"
        className={`toolbar-item views${itemHidden('views')}`}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          id="view"
          type="checkbox"
          name="views_ctrl"
          className="hidden input_toolbar input_views"
          tabIndex={-1}
          checked={openWindow === 'views'}
          readOnly
        />
        <div className="col-xs-12 p-0">
          {/* views.tmpl content (§2.2) — injected by MainView init in legacy. */}
          <div className="title">Views</div>
          <label className={`pin-view${pinned ? ' active' : ''}`} onClick={onPinClick}>
            <svg height="22" viewBox="0 -960 960 960" width="22">
              <path
                d="m640-480 80 80v80H520v240l-40 40-40-40v-240H240v-80l80-80v-280h-40v-80h400v80h-40v280Zm-286 80h252l-46-46v-314H400v314l-46 46Zm126 0Z"
                fill="#757575"
              />
            </svg>
          </label>
          <ul className="views_list">
            {VIEWS_LIST.map((v) => (
              <li
                key={v.value}
                className={`views_list_li${currentView === v.value ? ' active' : ''}`}
                data-value={v.value}
                onClick={() => onViewsListClick(v.value)}
              >
                <label>{v.label}</label>
              </li>
            ))}
            {/* defineCanvasViewport auto_360_li branch: proxies to the rail
                #auto_360 label (= toggleAuto360). NO disable class here —
                legacy views.tmpl:34 never carries one (the entry always looks
                active; the proxy click is simply dead until the rail enables).
                QA batch 4 #2: the extra `disable_rotate` gate greyed it out
                permanently. */}
            <li className="views_list_li auto_360_li" onClick={toggleAuto360}>
              <label>360</label>
            </li>
          </ul>
        </div>
      </div>

      <div
        id="model_3d"
        className={`toolbar-item model_3d generate_model${itemHidden('model_3d')}`}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          id="model_3d"
          type="checkbox"
          name="model_3d_ctrl"
          className="hidden input_toolbar input_model_3d"
          tabIndex={-1}
          checked={openWindow === 'model_3d'}
          readOnly
        />
        <div className="col-xs-12 p-0">
          <div className="title">Generate</div>
          <ul className="generate_model_list">
            {/* htmlFor=ahu3dGenerate forwards the click to the (gated) submit
                button in #cad_generate → POST /model/generate/ (§2.4
                model_btn_click; parity 28 localStorage pair). */}
            <li>
              <label
                data-type="3d"
                className="model_label"
                htmlFor={allowGenerate ? 'ahu3dGenerate' : undefined}
                onClick={() => selectModel3d(ahuId)}
              >
                3D Model
              </label>
            </li>
            <li>
              <label
                data-type="3d_td"
                className="model_label_3d"
                htmlFor={allowGenerate ? 'ahu3dGenerate' : undefined}
                onClick={() => selectModel3dTechData(ahuId)}
              >
                3D Model + Technical Data
              </label>
            </li>
          </ul>
        </div>
      </div>

      {/* Portaled out of #canvas_toolbar (see HybridInfoPopup) — rendered here
          only so the rail owns the open/minimized/closed state. */}
      {ahuIsHybrid && (
        <HybridInfoPopup
          state={infoState}
          onMinimize={() => setInfoState('minimized')}
          onRestore={() => setInfoState('open')}
          onClose={() => setInfoState('closed')}
        />
      )}
    </div>
  );
};

export default CanvasToolbar;
